import { Box, Link, List, ListItem, Stack, Typography } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";
import { createState } from "@/global/store/zustand_store";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { Divider } from "@/shared/base/components/Divider/Divider";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <TbShirtSport />,
    title: "Login to Account Manager",
    description: (
      <>
        Click Continue and connect your Iowa State University{" "}
        <Link
          target="_blank"
          href="https://am.ticketmaster.com/isu/"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          Account Manager
        </Link>{" "}
        using our secure Ticketmaster login.
      </>
    ),
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for your games.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your Iowa State University{" "}
        <Link
          href={"https://am.ticketmaster.com/isu/"}
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Account Manager
        </Link>{" "}
        within 24 hours of purchase. Reach out to Live Fan Support for any
        questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "When do sales begin?",
    description:
      "Cyclone Gameday Hospitality options are scheduled to go on sale beginning Monday, June 1.",
  },
  {
    title: "Can I purchase both Social at Reiman Gardens and a Reserve Tent?",
    description:
      "Yes. Guests may purchase from available Cyclone Gameday Hospitality options based on inventory and availability.",
  },
  {
    title: "Can I select my exact Reserve Tent location?",
    description:
      "Yes. Available Reserve Tent locations will be shown during the purchase process, allowing purchasers to select and secure a specific location while inventory remains available.",
  },
  {
    title: "Can Reserve Tent layouts be customized?",
    description:
      "No. Reserve Tent packages are offered as standardized, turnkey setups to provide a consistent and efficient gameday hospitality experience. Custom requests for alternate table, chair or furnishing layouts cannot be accommodated.",
  },
  {
    title: "Is Reserve Tent inventory limited?",
    description:
      "Yes. Due to the reduced hospitality footprint surrounding Jack Trice Stadium, Reserve Tent inventory is significantly limited and expected to be in high demand. Inventory will be available on a first-come, first-served basis.",
  },
  {
    title: "What is included with Social at Reiman Gardens?",
    description:
      "Social at Reiman Gardens is an all-inclusive, ticketed pregame experience including: all-inclusive food and beverages, live entertainment, access to Reiman Gardens, climate-controlled indoor space, and permanent restrooms on-site.",
  },
  {
    title: "Are there additional taxes or fees?",
    description:
      "Listed prices include all service fees. Applicable taxes will be added at the time of purchase.",
  },
];

// ---------------------------------------------------------------------------
// "More Info" modal (mirrors dbacks/yankees suite pattern) — text only.
// suiteNames match the raw `inventory.suiteName` returned by the API.
// ---------------------------------------------------------------------------

export const socialSuiteNames = ["SOCIAL"];

// Setup line varies by tent; same copy is shared within each group.
const tentSetupByGroup = {
  "1-3": `4x 50" Round tables w/ 32 chairs, 2x Cocktail tables & 2x 8' Banquet [catering]`,
  "4-6": `10x 50" Round tables w/ 80 chairs, 8x Cocktail tables & 4x 8' Banquet tables [catering]`,
  "7": `24x 50" Round tables w/ 192 chairs, 16x Cocktail tables & 8x 8' Banquet tables [catering]`,
  "8-10": `14x 50" Round tables w/ 112 chairs, 8x Cocktail tables & 4x 8' Banquet tables [catering]`,
};

// Per-tent setup copy (Tent 1..10), resolved from its group.
export const tentSetupForNumber = (n: number): string => {
  if (n <= 3) return tentSetupByGroup["1-3"];
  if (n <= 6) return tentSetupByGroup["4-6"];
  if (n === 7) return tentSetupByGroup["7"];
  return tentSetupByGroup["8-10"];
};

// Tent footprint (ft) per tent number, shown in the "Reserve Tent #" title.
export const tentSizeForNumber = (n: number): string => {
  if (n <= 3) return "20' x 30'";
  if (n <= 6) return "40' x 40'";
  if (n === 7) return "40' x 100'";
  return "40' x 60'";
};

export const inventoryCardSuiteModal = createState({
  title: "",
  isOpen: false,
  content: <></>,
});

export const InventoryCard = () => {
  const modal = inventoryCardSuiteModal();
  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={modal.title}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderBody={() => <Box p={1}>{modal.content}</Box>}
    />
  );
};

const ContentHeading = ({ title }: { title: string }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography
      variant="h6"
      sx={{
        fontWeight: 600,
        minWidth: "135px",
        fontSize: "1.25rem",
        color: "primary.main",
      }}
    >
      {title}
    </Typography>
    <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
  </Stack>
);

export const TentInfoContent = ({
  title,
  setup,
}: {
  title: string;
  setup: string;
}) => (
  <Box p={2} pt={1}>
    <ContentHeading title={title} />

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Your group. Your tent. Your gameday. Reserve Tents put you in the
          shadow of Jack Trice Stadium with a private, turnkey setup that&apos;s
          ready when you arrive — no logistics, no scramble, just a premium
          pregame experience for your crew.
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Available on a single-game basis, Reserve Tents are a limited
          collection of private hospitality spaces located just steps from the
          stadium. Guests can select their exact tent location during the
          purchase process, so you&apos;ll know exactly where you&apos;ll be
          gathering on gameday.
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • Setup: {setup}
          <br />• 2 parking passes and 1 vendor parking pass [caterer]
          <br />• Exact location selection during purchase
          <br />• Available on a single-game basis
          <br />• Jack Trice Stadium as your backdrop
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const SocialInfoContent = () => (
  <Box p={2} pt={1}>
    <ContentHeading title="Social at Reiman Gardens" />

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Forget the folding chairs and coolers. Social at Reiman Gardens is a
          turnkey pregame experience set within one of the most unique and
          beautiful environments on gameday — and it&apos;s all just steps from
          Jack Trice Stadium.
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          Whether it&apos;s a warm September opener or a crisp October
          afternoon, the climate-controlled indoor space at Reiman Gardens keeps
          you comfortable for every Social gameday. Show up, enjoy the
          atmosphere, and walk to the stadium ready to cheer.
        </Typography>
      </ListItem>

      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • All-inclusive food and beverages
          <br />• Live entertainment
          <br />• Access to Reiman Gardens
          <br />• Climate-controlled indoor space
          <br />• Permanent restrooms on-site
          <br />• No setup, no cleanup — just gameday
        </Typography>
      </ListItem>
    </List>
  </Box>
);
